/**
 * Archive Feedback
 *
 * Shared toast copy for archive/unarchive actions. Every archive call site
 * routes its success and failure feedback through here so the wording,
 * pluralization, and direction verb stay identical across `/search`, `/notes`,
 * `/experiments`, `/insights`, `/discovery`, and the sidebar recents rows.
 *
 * The copy is per-type, not global: each type's toast uses the verb printed on
 * the control the user pressed (experiments are "archived" and "restored",
 * everything else is "archived" and "unarchived").
 *
 * The notification manager is a `window` singleton assigned once in app.js and
 * is `undefined` in jsdom, so every call is optional-chained — matching the
 * convention in `shareContent.js` and every section call site.
 */

/**
 * Display noun per item type. `null`/unknown falls back to "item", which is
 * what a mixed-type batch (only reachable from /search select mode) gets.
 */
const TYPE_NOUNS = {
  session: ['Session', 'sessions'],
  note: ['Note', 'notes'],
  guide: ['Guide', 'guides'],
  thread: ['Thread', 'threads'],
  practice: ['Experiment', 'experiments']
};

const FALLBACK_NOUN = ['Item', 'items'];

/**
 * The verb for the direction that was applied.
 *
 * Only the un-archive direction differs by type, and only because the controls
 * do: /practices labels the pair "Archive" and "Restore" in the row menu, the
 * detail header and the bulk toolbar, while every other surface says
 * "Unarchive". Toast copy that contradicts the button reads as a bug.
 *
 * @param {boolean} archived - Direction applied (true = archived)
 * @param {string|null} type - Item type
 * @returns {string} Past-tense verb
 */
function directionVerb(archived, type) {
  if (archived) return 'archived';
  return type === 'practice' ? 'restored' : 'unarchived';
}

/**
 * Build the subject clause: "Note" / "3 notes" / "2 items".
 * @param {number} count - Number of items acted on
 * @param {string|null} type - Item type
 * @returns {string}
 */
function subject(count, type) {
  const [singular, plural] = TYPE_NOUNS[type] || FALLBACK_NOUN;
  return count === 1 ? singular : `${count} ${plural}`;
}

/**
 * Success toast for a completed archive/unarchive.
 *
 * Call only when the action actually succeeded. Sites that carry an inline
 * partial-failure notice (`/search`'s `actionNotice`, `/discovery`'s
 * `threadActionNotice`) must suppress this when that notice fires — a
 * "3 items archived" toast beside a "1 item couldn't be updated" notice is
 * contradictory.
 *
 * @param {Object} options
 * @param {number} [options.count=1] - Number of items acted on
 * @param {string|null} [options.type=null] - Item type; omit/null for mixed batches
 * @param {boolean} [options.archived=true] - Direction applied
 * @returns {void}
 */
export function notifyArchived({ count = 1, type = null, archived = true } = {}) {
  if (count < 1) return;
  window.notificationManager?.showSuccess(
    `${subject(count, type)} ${directionVerb(archived, type)}`
  );
}

/**
 * Failure toast for an archive/unarchive that did not land.
 *
 * Deliberately generic — no error codes reach the user. Sites with an inline
 * failure notice keep that instead; this is for the paths that previously
 * showed nothing at all.
 *
 * @param {Object} options
 * @param {number} [options.count=1] - Number of items that failed
 * @param {string|null} [options.type=null] - Item type
 * @param {boolean} [options.archived=true] - Direction that was attempted
 * @returns {void}
 */
export function notifyArchiveFailed({ count = 1, type = null, archived = true } = {}) {
  if (count < 1) return;
  const target = count === 1 ? 'that' : 'those';
  // Mirrors `directionVerb`: only the un-archive verb differs by type, because
  // /practices presses "Restore" where every other surface presses "Unarchive".
  const verb = archived
    ? 'archive'
    : (type === 'practice' ? 'restore' : 'unarchive');
  const phrase = `${verb} ${target}`;
  window.notificationManager?.showError(
    `Couldn't ${phrase}. Please try again.`
  );
}
